import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { localSafetyFormService, LocalSafetyForm } from '../services/localDataService';
import { safetyFormService } from '../services/amplifyDataService';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { toast } from 'react-toastify';
import './Screen.css';

export default function PTPApprove() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isOnline = useNetworkStatus();

  const [ptp, setPtp] = useState<LocalSafetyForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [superintendentName, setSuperintendentName] = useState('');
  const [superintendentPhone, setSuperintendentPhone] = useState('');
  const [superintendentSignature, setSuperintendentSignature] = useState<SignatureCanvas | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadPTP(id);
    }
  }, [id]);

  // Sync queue when coming back online
  const loadPTP = async (ptpId: string) => {
    try {
      // Try Amplify DataStore first (Gen 1)
      try {
        const form = await safetyFormService.getById(ptpId);
        if (form) {
          setPtp(form as any);
          setReviewNotes((form as any).endOfShiftReview || '');
          setLoading(false);
          return;
        }
      } catch (amplifyError) {
        // Fallback to local service
        console.log('Amplify DataStore not available, using local service');
      }
      
      const form = await localSafetyFormService.getById(ptpId);
      if (form) {
        setPtp(form);
        setReviewNotes(form.endOfShiftReview || '');
      }
    } catch (error) {
      console.error('Failed to load PTP:', error);
      toast.error('Failed to load PTP');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!superintendentName.trim() || !superintendentSignature) {
      alert('Please enter your name and provide a signature');
      return;
    }

    setSaving(true);
    try {
      const signatureData = superintendentSignature.toDataURL();

      const updates = {
        superintendentSignature: signatureData,
        superintendentReviewDate: new Date().toISOString(),
        superintendentEndPhone: superintendentPhone,
        endOfShiftReview: reviewNotes,
        status: 'approved' as const,
      };

      // Use Amplify DataStore (Gen 1) - handles offline sync automatically
      try {
        await safetyFormService.update(id!, updates);
        toast.success('PTP approved successfully');
        // DataStore automatically handles offline sync - no custom code needed!
      } catch (amplifyError: any) {
        // Fallback to local storage if Amplify not available
        console.warn('Amplify DataStore not available, using local storage:', amplifyError);
        await localSafetyFormService.update(id!, {
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        toast.info('PTP approved locally (AWS not configured)');
      }

      navigate('/ptp');
    } catch (error: any) {
      console.error('Failed to approve PTP:', error);
      toast.error(`Failed to approve PTP: ${error?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!reviewNotes.trim()) {
      alert('Please provide review notes explaining why the PTP is rejected');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this PTP? It will be returned to draft status.')) {
      return;
    }

    setSaving(true);
    try {
      const updates = {
        endOfShiftReview: reviewNotes,
        status: 'draft' as const,
      };

      // Use Amplify DataStore (Gen 1) - handles offline sync automatically
      try {
        await safetyFormService.update(id!, updates);
        toast.success('PTP rejected successfully');
        // DataStore automatically handles offline sync - no custom code needed!
      } catch (amplifyError: any) {
        // Fallback to local storage if Amplify not available
        console.warn('Amplify DataStore not available, using local storage:', amplifyError);
        await localSafetyFormService.update(id!, {
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        toast.info('PTP rejected locally (AWS not configured)');
      }

      alert('PTP rejected and returned to draft status.');
      navigate('/ptp');
    } catch (error) {
      console.error('Failed to reject PTP:', error);
      alert('Failed to reject PTP. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading PTP...</div>;
  }

  if (!ptp) {
    return (
      <div className="screen">
        <div className="error-message">PTP not found</div>
        <button onClick={() => navigate('/ptp')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Superintendent Review & Approval</h1>
        <p className="screen-subtitle">Review and approve Pre-Task Planning form (UC-4)</p>
      </div>

      {/* PTP Summary */}
      <div className="ptp-info-card">
        <h3>PTP Summary</h3>
        <div className="info-grid">
          <div><strong>Project:</strong> {ptp.projectName || 'N/A'}</div>
          <div><strong>Location:</strong> {ptp.location || 'N/A'}</div>
          <div><strong>Work Date:</strong> {ptp.workDate ? new Date(ptp.workDate).toLocaleDateString() : 'N/A'}</div>
          <div><strong>Foreman:</strong> {ptp.foremanName || 'N/A'}</div>
          <div><strong>Status:</strong> <span className={`badge badge-${ptp.status === 'completed' ? 'info' : 'warning'}`}>{ptp.status || 'draft'}</span></div>
          <div><strong>Created:</strong> {ptp.createdAt ? new Date(ptp.createdAt).toLocaleString() : 'N/A'}</div>
        </div>
      </div>

      {/* Required Permits */}
      {ptp.requiredPermits && ptp.requiredPermits.length > 0 && (
        <div className="form-section">
          <h2>Required Permits</h2>
          <ul className="checklist">
            {ptp.requiredPermits.map((permit, index) => (
              <li key={index}>✓ {permit}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Hazards */}
      {ptp.hazards && ptp.hazards.length > 0 && (
        <div className="form-section">
          <h2>Identified Hazards</h2>
          <ul className="checklist">
            {ptp.hazards.map((hazard, index) => (
              <li key={index}>⚠️ {hazard}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Control Measures */}
      {ptp.controlMeasures && ptp.controlMeasures.length > 0 && (
        <div className="form-section">
          <h2>Control Measures</h2>
          <ul className="checklist">
            {ptp.controlMeasures.map((measure, index) => (
              <li key={index}>✓ {measure}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Team Members */}
      {ptp.teamMembers && ptp.teamMembers.length > 0 && (
        <div className="form-section">
          <h2>Team Members</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Signed</th>
                </tr>
              </thead>
              <tbody>
                {ptp.teamMembers.map((member, index) => {
                  let crewSignatures: Array<{ name: string; signature: string; timestamp: string }> = [];
                  if (ptp.crewSignatures) {
                    if (typeof ptp.crewSignatures === 'string') {
                      try {
                        crewSignatures = JSON.parse(ptp.crewSignatures);
                      } catch {
                        crewSignatures = [];
                      }
                    } else if (Array.isArray(ptp.crewSignatures)) {
                      crewSignatures = ptp.crewSignatures;
                    }
                  }
                  const signature = crewSignatures.find(s => s.name === member);
                  return (
                    <tr key={index}>
                      <td>{member}</td>
                      <td>
                        {signature ? (
                          <span className="badge badge-success">
                            ✓ Signed {new Date(signature.timestamp).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="badge badge-warning">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* End of Day Status */}
      {ptp.nobodyHurtAcknowledgment && (
        <div className="form-section">
          <h2>End of Day Sign-Out</h2>
          <div className="info-box success">
            <p><strong>✓ Nobody Hurt Acknowledgment:</strong> Completed</p>
            <p><strong>Date:</strong> {ptp.nobodyHurtDate ? new Date(ptp.nobodyHurtDate).toLocaleString() : 'N/A'}</p>
          </div>
        </div>
      )}

      {/* Review Notes */}
      <div className="form-section">
        <h2>Review Notes</h2>
        <div className="form-group">
          <label>Review Comments</label>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={5}
            placeholder="Enter your review comments, observations, or approval notes..."
          />
        </div>
      </div>

      {/* Superintendent Information */}
      <div className="form-section">
        <h2>Superintendent Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Superintendent Name *</label>
            <input
              type="text"
              value={superintendentName}
              onChange={(e) => setSuperintendentName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="tel"
              value={superintendentPhone}
              onChange={(e) => setSuperintendentPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Digital Signature *</label>
          <div className="signature-container">
            <SignatureCanvas
              ref={(ref: any) => setSuperintendentSignature(ref)}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas',
              }}
            />
            <div className="signature-actions">
              <button
                type="button"
                onClick={() => superintendentSignature?.clear()}
                className="btn btn-secondary"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Actions */}
      <div className="form-actions">
        <button onClick={() => navigate('/ptp')} className="btn btn-secondary" disabled={saving}>
          Cancel
        </button>
        <button
          onClick={handleReject}
          className="btn btn-danger"
          disabled={saving || !reviewNotes.trim()}
        >
          {saving ? 'Processing...' : 'Reject & Return to Draft'}
        </button>
        <button
          onClick={handleApprove}
          className="btn btn-success"
          disabled={saving || !superintendentName.trim() || !superintendentSignature}
        >
          {saving ? 'Approving...' : '✓ Approve PTP'}
        </button>
      </div>

      {!isOnline && (
        <div className="offline-notice">
          ⚠️ You are offline. Approval will be saved locally and synced when online.
        </div>
      )}
    </div>
  );
}

